/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace beta
*/
var beta = {};

/**
* @name cdf
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/cdf}
*/
import cdf from './../../../../base/dists/beta/cdf';
setReadOnly( beta, 'cdf', cdf );

/**
* @name Beta
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/ctor}
*/
import Beta from './../../../../base/dists/beta/ctor';
setReadOnly( beta, 'Beta', Beta );

/**
* @name entropy
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/entropy}
*/
import entropy from './../../../../base/dists/beta/entropy';
setReadOnly( beta, 'entropy', entropy );

/**
* @name kurtosis
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/kurtosis}
*/
import kurtosis from './../../../../base/dists/beta/kurtosis';
setReadOnly( beta, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/logcdf}
*/
import logcdf from './../../../../base/dists/beta/logcdf';
setReadOnly( beta, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/logpdf}
*/
import logpdf from './../../../../base/dists/beta/logpdf';
setReadOnly( beta, 'logpdf', logpdf );

/**
* @name mean
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/mean}
*/
import mean from './../../../../base/dists/beta/mean';
setReadOnly( beta, 'mean', mean );

/**
* @name median
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/median}
*/
import median from './../../../../base/dists/beta/median';
setReadOnly( beta, 'median', median );

/**
* @name mgf
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/mgf}
*/
import mgf from './../../../../base/dists/beta/mgf';
setReadOnly( beta, 'mgf', mgf );

/**
* @name mode
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/mode}
*/
import mode from './../../../../base/dists/beta/mode';
setReadOnly( beta, 'mode', mode );

/**
* @name pdf
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/pdf}
*/
import pdf from './../../../../base/dists/beta/pdf';
setReadOnly( beta, 'pdf', pdf );

/**
* @name quantile
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/quantile}
*/
import quantile from './../../../../base/dists/beta/quantile';
setReadOnly( beta, 'quantile', quantile );

/**
* @name skewness
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/skewness}
*/
import skewness from './../../../../base/dists/beta/skewness';
setReadOnly( beta, 'skewness', skewness );

/**
* @name stdev
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/stdev}
*/
import stdev from './../../../../base/dists/beta/stdev';
setReadOnly( beta, 'stdev', stdev );

/**
* @name variance
* @memberof beta
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/beta/variance}
*/
import variance from './../../../../base/dists/beta/variance';
setReadOnly( beta, 'variance', variance );


// EXPORTS //

export default beta;
